import React from 'react';
import { Tag, Button, Popover, Tooltip } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getCriteriaLabelKey,
  shouldHideCriteriaField,
} from '@client/feature/treatment/utils/criteriaLabels';

const InitialCriteriaPanel = ({ searchData, onBackToSteps, searchMode = 'rapide' }) => {
  const { t } = useTranslation();

  if (!searchData || !searchData.selections) {
    return null;
  }

  const activeSelections = Object.entries(searchData.selections || {}).filter(
    ([field, values]) =>
      values && values.length > 0 && !shouldHideCriteriaField(field, searchMode)
  );

  if (activeSelections.length === 0) {
    return null;
  }

  const getFieldLabel = (field) => {
    const translationKey = getCriteriaLabelKey(field, searchMode);
    if (translationKey) return t(translationKey);
    return field.replace('_', ' ');
  };

  return (
    <div className="filters-container !mb-0">
      <Button
        type="text"
        size="small"
        icon={<ArrowLeftOutlined />}
        onClick={onBackToSteps}
        className="flex items-center gap-1 text-gray-600 hover:text-blue-600 px-2 py-1 h-auto"
      >
        {t('actions.back_to_search', 'Retour à la recherche')}
      </Button>
      <div className="flex items-center gap-2 mb-4 p-4 pb-2">
        <h4 className="normal-case text-base sm:text-lg md:text-xl font-medium break-words">
          {t('treatment-search|Résumé des critères de recherche')}
        </h4>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-4 pb-4">
        {activeSelections.map(([field, values]) => {
          const labels = searchData.selectionLabels?.[field] || [];
          return (
            <div key={field} className="space-y-2 min-w-0">
              <div className="font-medium text-gray-800 text-[13px] sm:text-[14px] break-words">
                {getFieldLabel(field)}
              </div>
              <div className="flex items-center gap-1">
                {(() => {
                  const items = labels.length > 0
                    ? labels.map((label, index) => ({
                        key: index,
                        text: label.label || label.name || values[index],
                      }))
                    : values.map((value, index) => ({
                        key: index,
                        text: value,
                      }));

                  const maxVisible = 1;
                  const visibleItems = items.slice(0, maxVisible);
                  const remainingItems = items.slice(maxVisible);

                  return (
                    <>
                      {visibleItems.map((item) => (
                        <Tooltip key={item.key} title={item.text}>
                          <Tag className="mb-0 max-w-[200px] sm:max-w-[260px] overflow-hidden text-ellipsis whitespace-nowrap block">
                            {item.text}
                          </Tag>
                        </Tooltip>
                      ))}
                      {remainingItems.length > 0 && (
                        <Popover
                          content={
                            <div className="flex flex-col gap-1" style={{ maxWidth: '500px', maxHeight: '300px', overflowY: 'auto' }}>
                              {remainingItems.map((item) => (
                                <Tag key={item.key} className="whitespace-normal break-words w-fit">{item.text}</Tag>
                              ))}
                            </div>
                          }
                          trigger={['hover', 'click']}
                          placement="bottom"
                        >
                          <Tag className="cursor-pointer mb-0">
                            +{remainingItems.length}
                          </Tag>
                        </Popover>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InitialCriteriaPanel;
